import styled from 'styled-components'
import React, { useEffect, useRef } from 'react'
import * as d3 from 'd3'

type LineChartProps = {
  data: DataEntry[]
  colorMode: 'light' | 'dark' | undefined
  title?: string
  subtitle?: string
  lang: string
}
type DataEntry = {
  year: number | null
  '\u2300 CH': number | null
  AI: number | null
  BS: number | null
  TI: number | null
  ZH: number | null
  GE: number | null
}

type DataEntryDE = {
  year: number | null
  '\u2300 CH': number | null
  AI: number | null
  BS: number | null
  TI: number | null
  ZH: number | null
}

type DataEntryFR = {
  year: number | null
  '\u2300 CH': number | null
  AI: number | null
  BS: number | null
  TI: number | null
  GE: number | null
}

const LineChartCanton = ({
  data,
  colorMode,
  title,
  subtitle,
  lang,
}: LineChartProps) => {
  const ref = useRef<SVGSVGElement>()
  const wrapperRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!ref.current || !data) return
    let tooltip = d3.select(wrapperRef.current).selectAll('.tooltip') as any

    if (tooltip.empty()) {
      // Define font-size based on window width
      let fontSize
      if (window.innerWidth <= 320) {
        fontSize = '12.5px'
      } else if (window.innerWidth < 712) {
        fontSize = '13.5px'
      } else {
        fontSize = '14px'
      }
      // Tooltip Element erstellen
      tooltip = d3
        .select(wrapperRef.current)
        .append('div')
        .attr('class', 'tooltip')
        .style('position', 'absolute')
        .style('z-index', '400')
        .style('background-color', 'white')
        .style(
          'box-shadow',
          `${
            colorMode === 'dark' ? '0 4px 40px #000000' : '0 4px 40px #00000033'
          }`
        )
        .style('border-radius', '5px')
        .style('opacity', 0.01)
        .style('pointer-events', 'none') // Add this line

        .style('font-size', fontSize)
        .style('font-family', 'IBM Plex Sans')
        .style('line-height', '1.2')
    }

    const margin = { top: 30, right: 47, bottom: 30, left: 7 } // increase top margin
    const width = ref.current.clientWidth - margin.left - margin.right
    const height = ref.current.clientHeight - margin.top - margin.bottom // adjust the height

    const svg = d3.select(ref.current)
    svg.selectChildren().remove()

    const chart = svg
      .append('g')
      .attr('transform', `translate(5, ${margin.top})`) // adjust translation here

    // Define startDomain based on window width
    let startDomain = window.innerWidth < 600 ? 1872 : 1875
    let endDomain = window.innerWidth < 600 ? 2025 : 2023

    let x = d3.scaleLinear().domain([startDomain, endDomain]).range([0, width])
    let xTicks = [1900, 1950, 2000]

    svg.on('mousemove touchmove', function (event) {
      var eventType = event.type === 'touchmove' ? event.touches[0] : event

      const x0 = x.invert(d3.pointer(eventType, wrapperRef.current)[0])

      // Filter out data where just year is in it
      let filteredData = data.filter(
        (entry: DataEntry) =>
          entry.year !== null &&
          (entry['\u2300 CH'] !== null ||
            entry.AI !== null ||
            entry.BS !== null ||
            entry.TI !== null ||
            entry.ZH !== null)
      )

      // Check if year is within required range
      if (x0 < 1885 || x0 > 2027) {
        return
      }

      // Get nearest year
      const bisect = d3.bisector((d: DataEntry) => d.year).left
      const i = bisect(filteredData, x0, 1)
      const d0 = filteredData[i - 1]
      const d1 = filteredData[i]

      let d = d0

      if (d1 && d1.year) {
        d = x0 - d0.year! > d1.year! - x0 ? d1 : d0
      }

      // From here on we have d as the nearest data point
      const xPos = x(d.year ?? 0)

      hideTooltip()

      // Add a vertical line
      chart
        .append('line')
        .attr('class', 'tooltip-line')
        .style('stroke', 'black')
        .style('stroke-width', '1px')
        .style('stroke', 'var(--chart-grid)')
        .style('pointer-events', 'none') // Add this line
        .attr('x1', xPos)
        .attr('y1', 0)
        .attr('x2', xPos)
        .attr('y2', height)

      // Make all dots of this year visible
      svg.selectAll(`.dot-year-${d.year}`).style('opacity', 1).raise() // Moves these dots to the front
      const chartWrapperPosition =
        wrapperRef?.current?.getBoundingClientRect() ?? { left: 0, top: 0 }

      // Crate color Map iterate over colors
      const colorMap: Partial<Record<string, (typeof colors)[number]>> = {}
      dataKeys.forEach((key, index) => {
        if (colors[index]) {
          colorMap[key] = colors[index]
        }
      })

      // Update tooltip information

      // sort dataKeys by Height
      let sortedDataKeys = [...dataKeys].sort(
        (a, b) => (d[b] ?? 0) - (d[a] ?? 0)
      )

      let htmlContent = `
      <div style='padding-bottom: 0.25em; background: ${
        colorMode === 'dark' ? '#555555' : '#e9e9e9'
      }; color: ${
        colorMode === 'dark' ? '#e9e9e9' : 'var(--text-color)'
      }; padding: 8px 12px; border-radius: 3px 3px 0 0;'>
          <b>${d.year}</b>
      </div>
      `
      htmlContent += `<div style='padding: 8px 12px; line-height: 1.4; background: ${
        colorMode === 'dark' ? '#242424' : '#ffffff'
      }'>`

      // Adding all datapoints in the tooltip
      sortedDataKeys.forEach((key) => {
        if (d[key] !== 0 && d[key] !== null) {
          htmlContent += `<span style='color: ${
            colorMap[key]
          }; display: inline-block;'><b>${key}</b>: ${(d[key] ?? 0)
            .toFixed(1)
            .replace('.', ',')}&nbsp;cm</span><br/>`
        }
      })
      htmlContent += `</div>`

      // Define leftPositionOffset based on window width
      let leftPositionOffset = window.innerWidth < 600 ? 140 : 150

      const leftPosition =
        ((d.year ?? 0) > 1960
          ? eventType.pageX - leftPositionOffset
          : eventType.pageX + 35) -
        window.scrollX -
        chartWrapperPosition.left
      const topPositionDesktop =
        eventType.pageY - window.scrollY - chartWrapperPosition.top + 40
      // top position for mobile should be on top of the chart
      const topPositionMobile = 170
      tooltip
        .style('opacity', 1)
        .html(htmlContent)
        .style('left', `${leftPosition}px`)
        .style(
          'top',
          (window.innerWidth < 768
            ? topPositionMobile
            : topPositionDesktop - 45) + 'px'
        )
    })

    svg.on('mouseleave', (event) => {
      hideTooltip()
    })

    chart
      .append('g')
      .attr('class', 'x axis-grid')
      .attr('transform', `translate(0, ${height})`)
      .call(
        d3
          .axisBottom(x)
          .tickFormat(d3.format(''))
          .tickValues(xTicks)
          .tickSizeOuter(0)
      )
      // .call(xAxis)
      .selectAll('text')
      .style('text-anchor', 'middle')

    // Y axis
    let y = d3.scaleLinear().domain([150, 180]).range([height, 0]) // set range here
    const yAxisGrid = d3.axisLeft(y).tickSize(-width).ticks(4)

    chart
      .append('g')
      .attr('class', 'y axis-grid')
      .call(yAxisGrid)
      .call((g) => g.select('.domain').remove())
      .call((g) => {
        // Remove the tick line for first tick
        g.select('.tick:first-of-type line').remove()
        // Remove the tick text for first tick
        g.select('.tick:first-of-type text').remove()
        // add unit cm to the last tick
        g.select('.tick:last-of-type text').text(function (d) {
          return d + ' cm'
        })
      })
      .call((g) =>
        g
          .selectAll('.tick text')
          .attr('x', 0)
          .attr('dy', -4)
          .style('text-anchor', 'start')
      )

    // Line Generators
    const dataKeysDE: (keyof DataEntryDE)[] = [
      '\u2300 CH',
      'AI',
      'BS',
      'TI',
      'ZH',
    ]
    const dataKeysFR: (keyof DataEntryFR)[] = [
      '\u2300 CH',
      'AI',
      'BS',
      'TI',
      'GE',
    ]

    const dataKeys = lang === 'de' ? dataKeysDE : dataKeysFR

    // Colors for each line
    const colors = [
      `${colorMode === 'dark' ? '#e9e9e9' : '#202346'}`,
      '#E16D00',
      '#D3343F',
      '#A757A3',
      '#378EBD',
    ]
    const labelEndOfLine =
      lang === 'de'
        ? ['\u2300 CH', 'AI', 'BS', 'TI', 'ZH']
        : ['\u2300 CH', 'AI', 'BS', 'TI', 'GE']
    const lineTextPosition =
      lang === 'de'
        ? ['.4em', '1.7em', '-1.5em', '.6em', '-0.6em']
        : ['.2em', '1.4em', '-0.4em', '2.4em', '0.7em']
    //   .attr('dy', '1.65em')

    dataKeys.forEach((key, index) => {
      const lineData = data.filter((item) => item[key] !== null)

      const lineGenerator = d3
        .line<DataEntry>()
        .x((d) => x(d.year ?? 0))
        .y((d) => y(d[key] ?? 0))

      const path = chart
        .append('path')
        .attr('fill', 'none')
        .attr('stroke-width', 2.5)
        .attr('stroke', colors[index])
        .attr('d', lineGenerator(lineData))
      // Check if the data key is 'men_ch'
      if (key === '\u2300 CH') {
        // Add stroke-dasharray attribute to make the line dashed
        path.attr('stroke-dasharray', '5,5')
      }

      // Tooltip handlers for each line
      chart
        .selectAll(`.dot-${key}`)
        .data(lineData)
        .enter()
        .append('circle')
        .attr('class', function (d) {
          return `dot dot-${key} dot-year-${d.year}`
        })
        .style('fill', colors[index])
        .attr('cx', function (d) {
          return x(d.year ?? 0)
        })
        .attr('cy', function (d) {
          return y(d[key] ?? 0)
        })
        .attr('r', 5)
        .style('opacity', 0)

      // Define fontSize based on window width
      let fontSize = window.innerWidth < 712 ? '15px' : '16px'

      chart
        .append('text')
        .attr('text-anchor', 'start')
        .style('font-size', fontSize)
        .attr(
          'transform',
          `translate(${width + 12}, ${y(
            lineData[lineData.length - 1][key] ?? 0
          )})`
        )
        .attr('dy', lineTextPosition[index])
        .style('fill', colors[index])
        .text(labelEndOfLine[index])
    })

    const hideTooltip = () => {
      tooltip.style('opacity', 0)
      chart.selectAll('.tooltip-line').remove()
      svg.selectAll('.dot').style('opacity', 0.01)
    }

    // New code: Create an Intersection Observer to watch if the SVG is in view or not.
    var svgEl = d3.select(ref.current).node() // get SVG node
    var observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) {
          // Check if not intersecting
          hideTooltip()
        }
      })
    })

    if (svgEl !== null) {
      observer.observe(svgEl)
    }

    // only hide on scroll on desktop
    if (window.innerWidth > 768) {
      console.log('adding scroll eventlistener')
      window.addEventListener('scroll', hideTooltip)
      return () => {
        window.removeEventListener('scroll', hideTooltip)
        // Disconnect Observer on unmount
        observer.disconnect()
      }
    }
  }, [data, colorMode])

  return (
    <Wrapper ref={wrapperRef}>
      <Title>
        {title}
        <div className='subtitle'>{subtitle}</div>
      </Title>
      <LineChartContainer>
        <svg ref={ref as React.RefObject<SVGSVGElement>} />
      </LineChartContainer>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  position: relative;
`

const Title = styled.div`
  flex: 0 0 100%;
  font-size: 23px;
  line-height: 1.4em;
  font-weight: 200;
  letter-spacing: 0.6px;
  text-align: left;
  margin-bottom: 30px;

  .subtitle {
    display: inline-block;
    font-size: 13px;
    line-height: 1.4em;
    width: 100%;
    color: var(--subtitle);
    padding-top: 0.75em;
  }
`

const LineChartContainer = styled.div`
  height: 360px;
  width: 100%;
  text-align: center;
  font-family: var(--font-sans-serif);
  margin-bottom: 25px;
  svg {
    width: 100%;
    height: 100%;
  }
  .axis-grid line {
    stroke: var(--chart-grid);
    stroke-dasharray: 4, 2;
  }
  .tick text {
    font-size: 14px;
    fill: var(--text-color);
  }
  .x.axis-grid {
    .tick line {
      stroke: var(--text-color);
    }
  }
`

export default LineChartCanton
